import { Box, Divider, IconButton, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DOC_TYPES } from '../../../lib/packingListConstants';
import { headerMaxLength } from '../../../lib/packingListFields';
import { panelTitleSx, smallFieldSx } from 'shared/config/Styles';
import { reviewPanelHeadSx, reviewPanelSx } from '../../../lib/packingListReviewStyles';
import { headerConfidence } from '../../../lib/packingListWire';
import PartyCompareTable from './PartyCompareTable';
import ConfidenceMark from './ConfidenceMark';
import TotalCompareTable from './TotalCompareTable';
import LotEntryField from './LotEntryField';

const TABS = [
    { key: 'info', label: 'DOCUMENT & PARTIES' },
    { key: 'totals', label: 'TOTALS CHECK' },
    { key: 'lots', label: 'LOT' },
];

/** 선택된 탭의 바탕과 아래 경계선. 패널의 다른 강조(#00647e)와 같은 푸른 계열로 맞춘다. */
const SELECTED_TAB_BG = '#e3eff4';
const SELECTED_TAB_LINE = '#00647e';

/** 헤더 필드 한 칸. 파서 신뢰도가 낮은 칸은 그리드와 같은 점으로 알린다. */
function Field({ id, label, value, onChange, type = 'text', select, options, highlight, readOnly, confidence = null }) {
    return (
        <Box sx={{ position: 'relative', minWidth: 0 }}>
        <TextField
            id={id}
            size="small"
            fullWidth
            select={select}
            type={type}
            label={label}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly }}
            error={Boolean(highlight)}
            sx={smallFieldSx}
            inputProps={{ maxLength: headerMaxLength(id.replace('header-', '')) }}
        >
            {select && options.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 12 }}>{o}</MenuItem>)}
        </TextField>
            <ConfidenceMark confidence={confidence} field={id.replace(/^header-/, '')} edited={false} />
        </Box>
    );
}

/**
 * 문서 레벨 정보 패널. 세 탭(문서·당사자 / 합계 대조 / LOT 일괄)으로 나눠 라인 검수 중에도 필요한 것만 본다.
 * 접으면 인보이스·수량·중량 요약 한 줄만 남아 그리드에 공간을 내준다.
 * 문서 합계는 합계 대조 탭에서 라인 합계 옆에 두고 고친다.
 * readOnly(확정본 보기)면 모든 칸을 읽기만 하고, 일괄 입력 도구인 LOT 탭은 두지 않는다.
 */
export default function HeaderFormPanel({ header, onChange, totals = null, onTotalsChange, lines, houseShipper, houseConsignee, partyAliases, onRegisterAlias, onBatchApplyLot, highlightField, activeTab, onTabChange, expanded, onExpandedChange, readOnly = false }) {
    const sumQty = lines.reduce((s, l) => s + (Number(l.qty) || 0), 0);
    const tabs = readOnly ? TABS.filter((t) => t.key !== 'lots') : TABS;
    const tab = tabs.some((t) => t.key === activeTab) ? activeTab : 'info';
    const tabIndex = Math.max(0, tabs.findIndex((t) => t.key === tab));

    return (
        <Box sx={{ ...reviewPanelSx, height: '100%', minHeight: 0 }}>
            <Box sx={reviewPanelHeadSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography sx={panelTitleSx} noWrap>Document Info</Typography>
                    {!expanded && <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>Inv {header.invoice_no || '—'} · {header.total_qty ?? sumQty} {header.qty_uom || ''} · G.W {header.total_gross_weight ?? '—'} {header.weight_uom || ''}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Tabs value={tabIndex} onChange={(_, i) => { onTabChange(tabs[i].key); onExpandedChange(true); }} TabIndicatorProps={{ sx: { bgcolor: SELECTED_TAB_LINE } }} sx={{ minHeight: '35px', height: '36px', '& .MuiTabs-flexContainer': { gap: 0.5 }, '& .MuiButtonBase-root': { border: 'none', p: 0 } }}>
                        {tabs.map((t) => (
                            <Tab
                                key={t.key}
                                label={t.label}
                                sx={{
                                    width: '130px',
                                    minHeight: '35px',
                                    borderRadius: '5px',
                                    border: '1px solid #dfdfdf',
                                    bgcolor: 'white',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    '&.Mui-selected': { bgcolor: SELECTED_TAB_BG },
                                }}
                            />
                        ))}
                    </Tabs>
                    <IconButton size="small" onClick={() => onExpandedChange(!expanded)} title={expanded ? 'Collapse' : 'Expand'}>{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
                </Box>
            </Box>
            {expanded && (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
                    {tab === 'totals' && <TotalCompareTable header={header} lines={lines} totals={totals} onChange={onChange} onTotalsChange={onTotalsChange} highlightField={highlightField} readOnly={readOnly} />}
                    {tab === 'lots' && <LotEntryField lines={lines} onBatchApply={onBatchApplyLot} />}
                    {tab === 'info' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1 }}>
                                <Field id="header-doc_type" label="Doc Type *" select options={DOC_TYPES} value={header.doc_type} onChange={(v) => onChange('doc_type', v)} readOnly={readOnly} />
                                <Field id="header-invoice_no" confidence={headerConfidence(header, 'invoice_no')} label="Invoice No. *" value={header.invoice_no} onChange={(v) => onChange('invoice_no', v)} highlight={highlightField === 'invoice_no'} readOnly={readOnly} />
                                <Field id="header-invoice_date" confidence={headerConfidence(header, 'invoice_date')} label="Invoice Date" type="date" value={header.invoice_date} onChange={(v) => onChange('invoice_date', v)} readOnly={readOnly} />
                                <Field id="header-asn_no" confidence={headerConfidence(header, 'asn_no')} label="ASN No." value={header.asn_no} onChange={(v) => onChange('asn_no', v)} readOnly={readOnly} />
                                <Field id="header-country_of_origin" confidence={headerConfidence(header, 'country_of_origin')} label="Country of Origin" value={header.country_of_origin} onChange={(v) => onChange('country_of_origin', v)} readOnly={readOnly} />
                            </Box>
                            <Divider sx={{ borderColor: '#dfe3e6' }} />
                            <PartyCompareTable header={header} onChange={onChange} houseShipper={houseShipper} houseConsignee={houseConsignee} partyAliases={partyAliases} onRegisterAlias={onRegisterAlias} highlightField={highlightField} readOnly={readOnly} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}
